import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { RotationPattern } from '../../interfaces/shift/RotationPattern';
import { Shift } from '../../interfaces/shift/Shift';
import { RotationPatternService } from '../../services/rotation-pattern.service';
import { ShiftService } from '../../services/shift.service';
import RotationPatternEditDialog from './RotationPatternEditDialog';

// NOTE: No Edit button is shown because the backend has no update endpoint for rotation patterns.
// Users must delete and re-create a pattern to make changes.
// When a backend PATCH/PUT endpoint is added, restore the Edit IconButton and wire it here.

const RotationPatternListPage: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();

    const canRotate = usePermission(PERMISSIONS.SHIFT_ASSIGN);

    const [patterns, setPatterns] = useState<RotationPattern[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (selectedInstitutionId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchData = async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const [patternData, shiftData] = await Promise.all([
                RotationPatternService.list(selectedInstitutionId),
                ShiftService.list(selectedInstitutionId),
            ]);
            setPatterns(patternData);
            setShifts(shiftData);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch rotation patterns');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setDialogOpen(true);
    };

    const handleDelete = async (pattern: RotationPattern) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(`Delete rotation pattern "${pattern.name}"?`)) return;
        try {
            await RotationPatternService.remove(selectedInstitutionId, pattern.id);
            toast.showSuccess('Rotation pattern deleted');
            await fetchData();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete rotation pattern');
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleSaved = () => {
        fetchData();
    };

    if (loading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;

    return (
        <Box data-help-page="shifts.patterns.page" sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Rotation Patterns</Typography>
                {canRotate && (
                    <Button data-help="shifts.patterns.new" variant="contained" color="primary" onClick={handleCreate}>
                        + New Pattern
                    </Button>
                )}
            </Box>

            {patterns.length === 0 ? (
                <Typography>No rotation patterns found</Typography>
            ) : (
                <TableContainer data-help-section="shifts.patterns.table" component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="shifts.patterns.col.name">Name</TableCell>
                                <TableCell data-help="shifts.patterns.col.daysPerStep">Days / Step</TableCell>
                                <TableCell data-help="shifts.patterns.col.startDate">Start Date</TableCell>
                                <TableCell data-help="shifts.patterns.col.active">Active</TableCell>
                                <TableCell data-help="shifts.patterns.col.steps">Steps</TableCell>
                                {canRotate && <TableCell data-help="shifts.patterns.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {patterns.map(pattern => (
                                <TableRow key={pattern.id}>
                                    <TableCell>{pattern.name}</TableCell>
                                    <TableCell>{pattern.daysPerStep}</TableCell>
                                    <TableCell>{pattern.startDate}</TableCell>
                                    <TableCell>
                                        {pattern.isActive
                                            ? <Chip label="Active" color="success" size="small" />
                                            : <Chip label="Inactive" size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${pattern.shiftIdsInOrder.length} shift${pattern.shiftIdsInOrder.length !== 1 ? 's' : ''}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    {canRotate && (
                                        <TableCell>
                                            <IconButton
                                                data-help="shifts.patterns.action.delete"
                                                onClick={() => handleDelete(pattern)}
                                                color="error"
                                                aria-label="delete"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedInstitutionId && (
                <RotationPatternEditDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    institutionId={selectedInstitutionId}
                    shifts={shifts}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};

export default RotationPatternListPage;
